<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="z-modal relative" @close="closeDialog">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md"
            >
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10"
                  >
                    <ExclamationTriangleIcon
                      class="size-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900"
                    >
                      {{ title }}
                    </DialogTitle>
                    <div class="mt-2">
                      <p class="text-sm text-gray-500">{{ text }}</p>
                    </div>
                    <div v-if="dangerousAction" class="mt-4">
                      <Label for="confirm-dangerous-action">
                        {{
                          $t(
                            'components.modal-confirm-delete.confirm-dangerous-action-label'
                          ) + ' '
                        }}<kbd
                          class="inline-block rounded bg-gray-200 px-2 py-1.5 text-xs font-semibold"
                        >
                          {{ confirmDangerousActionText }}
                        </kbd>
                      </Label>
                      <Input
                        v-model="confirmDangerousActionInput"
                        id="confirm-dangerous-action"
                        :disabled="loading"
                        autocomplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="border-t border-t-gray-200 bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
              >
                <Button
                 
                  type="danger"
                  class="w-full sm:ml-3 sm:w-auto"
                  :loading="loading"
                  :disabled="!canDelete"
                  @click="confirmDelete"
                >
                  {{ $t('general.delete') }}
                </Button>
                <Button
                 
                  type="secondary"
                  class="mt-3 w-full sm:mt-0 sm:w-auto"
                  :disabled="loading"
                  @click="closeDialog"
                >
                  {{ $t('general.cancel') }}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  dangerousAction: {
    type: Boolean,
    default: false,
  },
  customConfirmText: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['update:modelValue', 'confirmed']);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const i18n = useI18n();

const confirmDangerousActionInput = ref('');

const confirmDangerousActionText = computed(
  () =>
    props.customConfirmText ||
    i18n.t('components.modal-confirm-delete.confirm-dangerous-action-text')
);

const canDelete = computed(() => {
  if (!props.dangerousAction) {
    return true;
  }

  return confirmDangerousActionInput.value === confirmDangerousActionText.value;
});

const closeDialog = () => {
  if (!props.loading) {
    open.value = false;
  }
};

const confirmDelete = () => {
  if (!props.loading && canDelete.value) {
    emit('confirmed');
  }
};

watch(open, (newValue) => {
  if (newValue) {
    confirmDangerousActionInput.value = '';
  }
});
</script>
